"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Philosophy() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const values = [{
    title: "Direct Acquisition",
    description: "We purchase residential properties directly from homeowners, ensuring a transparent, fair, and efficient process without the complexities of traditional brokerage listings.",
    tilt: "-rotate-[1.5deg]",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M16 5c-3.5 5-7 8.5-7 13a7 7 0 0 0 14 0c0-4.5-3.5-8-7-13z" />
          <path d="M13 19a3 3 0 0 0 3 3" />
        </svg>
  }, {
    title: "Value-Add Renovation",
    description: "Our team revitalizes residential properties through thoughtful improvements, enhancing long-term value and contributing positively to the local neighborhood aesthetic.",
    tilt: "rotate-0",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M4 22c2-2 4-2 6 0s4 2 6 0 4-2 6 0 4 2 6 0" />
          <path d="M20 8l-8 10" />
          <path d="M22 6l4 4" />
          <path d="M18 12l4 4" />
        </svg>
  }, {
    title: "Long-Term Stewardship",
    description: "We acquire rental properties for sustainable, long-term investment, prioritizing responsible management and the creation of stable, high-quality housing solutions.",
    tilt: "rotate-0",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <circle cx="16" cy="16" r="5" />
          <path d="M16 4v3" />
          <path d="M16 25v3" />
          <path d="M4 16h3" />
          <path d="M25 16h3" />
          <path d="M7.5 7.5l2 2" />
          <path d="M22.5 22.5l2 2" />
          <path d="M7.5 24.5l2-2" />
          <path d="M22.5 9.5l2-2" />
        </svg>
  }, {
    title: "Community Partnership",
    description: "We build lasting relationships with investors and community partners, fostering a culture of integrity, mutual growth, and responsible real estate development.",
    tilt: "rotate-[1.5deg]",
    icon: <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M6 22c4-12 14-16 22-16-1 10-6 18-16 18-3 0-6-1-6-2z" />
          <path d="M6 22c2-4 6-8 12-10" />
          <path d="M4 26c1-2 2-3 4-4" />
        </svg>
  }];
  return <section data-section-id="1147" id="our-investment-philosophy" className="dark relative overflow-hidden bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <svg className="absolute top-0 left-0 w-full h-6 text-background" viewBox="0 0 1320 24" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,24 C82.5,2 165,2 247.5,12 C330,22 412.5,2 495,8 C577.5,14 660,2 742.5,10 C825,18 907.5,2 990,12 C1072.5,22 1155,4 1237.5,10 C1280,13 1310,18 1320,24 L1320,0 L0,0 Z" fill="currentColor" />
      </svg>

      <svg className="absolute top-32 right-[10%] w-10 h-10 text-secondary/60 pointer-events-none" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
        <circle cx="16" cy="16" r="4" />
        <path d="M16 4v4M16 24v4M4 16h4M24 16h4M7 7l3 3M22 22l3 3M7 25l3-3M22 10l3-3" />
      </svg>
      <svg className="absolute bottom-40 right-[6%] w-12 h-12 text-secondary/50 pointer-events-none" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
        <path d="M4 24c4-4 8-4 12 0s8 4 12 0 8-4 12 0" />
      </svg>

      <div className="relative max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        <div ref={headRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="font-mono text-xs md:text-sm uppercase tracking-[0.18em] text-muted-foreground mb-6">
             01 — OUR INVESTMENT PHILOSOPHY
          </div>

          <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-foreground">
            Building legacy through
            <br />
            responsible{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic font-serif">investing</span>
              <svg className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 140 70" fill="none" aria-hidden="true">
                <ellipse cx="70" cy="36" rx="62" ry="28" stroke="var(--secondary)" strokeWidth="3" strokeLinecap="round" transform="rotate(-3 70 36)" strokeDasharray="320" strokeDashoffset={headVisible ? "0" : "320"} style={{
                transition: "stroke-dashoffset 700ms ease-out 300ms"
              }} />
              </svg>
            </span>
            .
          </h2>

          <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground mt-7 max-w-xl">
            Summertime Lane Investments is dedicated to long-term value creation. We prioritize integrity, professional stewardship, and community-focused growth in every residential property we acquire and manage.
          </p>

          <Link to="/HomePage" className="group inline-flex items-center gap-2 font-default font-semibold text-foreground mt-8 relative">
            <span className="relative">
              Learn about our approach
              <svg className="absolute left-0 -bottom-1.5 w-full h-2 text-secondary" viewBox="0 0 200 8" preserveAspectRatio="none" fill="none" aria-hidden="true">
                <path d="M2 5 Q 25 1, 50 5 T 100 5 T 150 5 T 198 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out" style={{
                transformBox: "fill-box"
              }} />
              </svg>
            </span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <div className="mt-12 lg:mt-16 w-24 h-24" aria-hidden="true">
            <svg viewBox="0 0 120 120" fill="none" className="w-full h-full">
              <path d="M20 80 Q 20 40 60 40 Q 100 40 100 80 Q 100 95 85 95 L 35 95 Q 20 95 20 80 Z" fill="var(--card)" stroke="var(--secondary)" strokeWidth="2.5" strokeLinejoin="round" />
              <path d="M48 40 Q 50 30 58 32 Q 62 24 70 30 Q 78 28 76 38" fill="var(--background)" stroke="var(--secondary)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              <circle cx="48" cy="65" r="3" fill="var(--secondary)" />
              <circle cx="72" cy="65" r="3" fill="var(--secondary)" />
              <circle cx="40" cy="76" r="4" fill="var(--secondary)" opacity="0.7" />
              <circle cx="80" cy="76" r="4" fill="var(--secondary)" opacity="0.7" />
              <path d="M52 78 Q 60 84 68 78" stroke="var(--secondary)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            </svg>
          </div>
        </div>

        <div ref={gridRef} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {values.map((value, i) => <div key={i} data-index={i} className={`group relative bg-card text-card-foreground rounded-lg p-7 md:p-9 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 ease-out sm:${value.tilt} hover:rotate-0 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transition: `opacity 700ms ease-out ${i * 90}ms, transform 320ms ease-out, box-shadow 320ms ease-out`,
          opacity: gridVisible ? 1 : 0,
          transform: gridVisible ? undefined : "translateY(16px)"
        }}>
              <svg className="absolute top-3 right-3 w-10 h-10 text-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <path d="M8 8 Q 20 4, 32 8 M 32 8 Q 36 20, 32 32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              </svg>

              <div className="text-secondary mb-5">{value.icon}</div>

              <h3 className="font-default font-semibold text-xl md:text-[1.375rem] leading-snug text-foreground mb-3">
                {value.title}
              </h3>

              <p className="font-default text-base leading-relaxed text-muted-foreground">
                {value.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}